import { Box, Chip } from "@mui/material";
import { DateTime } from "luxon";

/** Monday to Sunday, in the 0 to 6 the API and the parser speak. */
export const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

/** Every day: what a selection that names no day means. */
export const ANY_WEEKDAY = WEEKDAYS;

/** What unselecting the last day does: select them all, or refuse. */
export type EmptySelection = "all" | "ignore";

const label = (day: number) =>
  DateTime.now().startOf("week").plus({ days: day }).toFormat("ccc");

/**
 * Filter behaviour rather than checkbox behaviour: from "any day", one click
 * means "only that one".
 *
 * A selection is never empty, but the two callers mean different things by
 * that. A filter reads "no day chosen" as "any day", so the last removal
 * selects them all. A schedule cannot run on no day at all and would rather
 * ignore the click than start running every day, hence `onEmpty`.
 */
export const toggleWeekday = (
  weekdays: number[],
  day: number,
  onEmpty: EmptySelection = "all"
): number[] => {
  if (weekdays.length === WEEKDAYS.length) {
    return [day];
  }
  if (!weekdays.includes(day)) {
    return WEEKDAYS.filter(
      (weekday) => weekdays.includes(weekday) || weekday === day
    );
  }
  const kept = weekdays.filter((weekday) => weekday !== day);
  if (kept.length > 0) {
    return kept;
  }
  return onEmpty === "all" ? ANY_WEEKDAY : weekdays;
};

/**
 * The days of the week, as chips.
 *
 * Read-only in a grid cell, clickable in a form. The two used to be different
 * components: single letters in the autopilot table and three-letter chips in
 * the group studio, which is one control drawn twice.
 */
const WeekdayPicker = ({
  value,
  onChange,
  onEmpty = "all",
  size = "small",
}: {
  value: number[];
  /** Omitted for a read-only rendering, as in a table cell. */
  onChange?: (weekdays: number[]) => void;
  onEmpty?: EmptySelection;
  size?: "small" | "medium";
}) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
    {WEEKDAYS.map((day) => (
      <Chip
        key={day}
        size={size}
        label={label(day)}
        color={value.includes(day) ? "primary" : "default"}
        variant={value.includes(day) ? "filled" : "outlined"}
        onClick={
          onChange && (() => onChange(toggleWeekday(value, day, onEmpty)))
        }
      />
    ))}
  </Box>
);

export default WeekdayPicker;
